<template>
  <MetaInfo
    :title="meta.title"
    :description="meta.description"
  ></MetaInfo>

  <div class="support-page">
    <navigation></navigation>

    <!-- Left Sidebar -->
    <support-sidebar></support-sidebar>

    <div class="support-content">
      <div class="container-fluid">
        <div class="row">
          <div class="col-sm-12">
            <div class="support-docs__header text-center">
              <h1 class="h3">Support Docs</h1>
              <p class="p--lg">Learn how to use Transpond and get the most out of your account</p>
            </div>

          <!-- Error Message -->
          <div v-if="error" class="support-error">
            <h3>Error Loading Documentation</h3>
            <p><strong>{{ error.message }}</strong></p>
            <p v-if="error.isAuthError">
              Please check that your HelpScout API key is correctly configured in your <code>.env.local</code> file.
            </p>
          </div>

          <!-- Loading State -->
          <div v-if="loading && !error">
            <skeleton-loader type="docs" :item-count="3"></skeleton-loader>
          </div>

          <!-- Collections List -->
          <div v-if="!loading && !error && collections.length > 0" class="support-collections">
            <div class="support-collections__grid">
              <router-link 
                v-for="collection in collections" 
                :key="collection.id" 
                :to="`/support/${collection.slug || collection.id}`"
                class="support-collections__card"
              >
                <div class="support-collections__icon" v-html="getIcon(collection.slug)"></div>
                <h3>{{ collection.name }}</h3>
                <p class="support-collections__description">
                  {{ collection.description || 'Browse articles in this collection.' }}
                </p>
              </router-link>
            </div>
          </div>

          <!-- No Data State -->
          <div v-if="!loading && !error && collections.length === 0" class="support-empty">
            <p>No documentation collections found.</p>
          </div>

          <div v-if="!loading" class="support-contact">
            <div class="support-contact__icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>
            </div>
            <div class="support-contact__copy">
              <h2 class="support-contact__title">Contact support</h2>
              <p class="support-contact__text">Can&rsquo;t find what you need in the docs? Send a message and the team will help you sort it out.</p>
            </div>
            <router-link class="btn btn--tertiary btn--sm support-contact__action" to="/contactus/">
              Get in touch
            </router-link>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import SupportSidebar from "@/components/Support/SupportSidebar";
import SkeletonLoader from "@/components/Support/SkeletonLoader";
import helpScoutService from "@/services/helpscout";
import helpScoutState from "@/services/helpscoutState";

const collectionIcons = {
  "deliver": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"/><path d="m21.854 2.147-10.94 10.939"/></svg>',
  "deliverability-and-compliance": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>',
  "growing-your-audience": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 7h6v6"/><path d="m22 7-8.5 8.5-5-5L2 17"/></svg>',
  "account-and-user-management": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="m14.305 19.53.923-.382"/><path d="m15.228 16.852-.923-.383"/><path d="m16.852 15.228-.383-.923"/><path d="m16.852 20.772-.383.924"/><path d="m19.148 15.228.383-.923"/><path d="m19.53 21.696-.382-.924"/><path d="M2 21a8 8 0 0 1 10.434-7.62"/><path d="m20.772 16.852.924-.383"/><path d="m20.772 19.148.924.383"/><circle cx="10" cy="8" r="5"/><circle cx="18" cy="18" r="3"/></svg>',
  "engage": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12.7 3H4a2 2 0 0 0-2 2v16.286a.71.71 0 0 0 1.212.502l2.202-2.202A2 2 0 0 1 6.828 19H20a2 2 0 0 0 2-2v-4.7"/><circle cx="19" cy="6" r="3"/></svg>',
  "managing-your-audience": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 21a8 8 0 0 0-16 0"/><circle cx="10" cy="8" r="5"/><path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3"/></svg>',
  "creating-and-sending-campaigns": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="7" x="3" y="3" rx="1"/><rect width="9" height="7" x="3" y="14" rx="1"/><rect width="5" height="7" x="16" y="14" rx="1"/></svg>',
  "reporting-and-performance-insights": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M7 16c.5-2 1.5-7 4-7 2 0 2 3 4 3 2.5 0 4.5-5 5-7"/></svg>',
  "integrations": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6.3 20.3a2.4 2.4 0 0 0 3.4 0L12 18l-6-6-2.3 2.3a2.4 2.4 0 0 0 0 3.4Z"/><path d="m2 22 3-3"/><path d="M7.5 13.5 10 11"/><path d="M10.5 16.5 13 14"/><path d="m18 3-4 4h6l-4 4"/></svg>',
  "setup-and-configuration": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"/><circle cx="12" cy="12" r="3"/></svg>',
  "automating-your-communications": '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="8" x="3" y="3" rx="2"/><path d="M7 11v4a2 2 0 0 0 2 2h4"/><rect width="8" height="8" x="13" y="13" rx="2"/></svg>',
};
const defaultIcon = collectionIcons["creating-and-sending-campaigns"];

export default {
  name: "SupportDocsPage",
  components: {
    MetaInfo,
    Navigation,
    SupportSidebar,
    SkeletonLoader,
  },
  data() {
    return {
      collections: [],
      loading: true,
      error: null,
      debugData: null,
      meta: {
        title: "",
        description: "",
      },
    };
  },
  async created() {
    await this.loadCollections();
  },
  methods: {
    getIcon(slug) {
      return collectionIcons[slug] || defaultIcon;
    },
    async loadCollections() {
      try {
        this.loading = true;
        this.error = null;

        // Use shared state instead of direct API call
        this.collections = await helpScoutState.getCollections(helpScoutService);
        this.debugData = { collections: this.collections };
        this.meta.title = "Support Documentation";
        this.meta.description =
          "Browse our support documentation and help articles.";

      } catch (error) {
        console.error('Error loading collections:', error);
        
        // Check if it's an authentication error
        const isAuthError = error.response && (error.response.status === 401 || error.response.status === 403);
        
        this.error = {
          message: error.message || 'Failed to load documentation',
          isAuthError,
          details: error.response ? {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          } : null
        };

        this.debugData = { error: this.error };
        this.meta.title = "Support Documentation";
        this.meta.description =
          "Browse our support documentation and help articles.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
